import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../scss/loyalty-rules-section.component.scss';
import { LOCALES } from '../../common/constants';
import { formatAmount } from '../helper/common.helper';

const LoyaltyRulesSection: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState('howToEarn');
    const currentLanguage = i18n.language;
    const isEnglish = currentLanguage === LOCALES.EN_US;
    const tabs = [
        {
            key: 'howToEarn',
            label: 'LOYALTY_PROGRAM_HOW_TO_EARN_POINTS',
            mobileLabel: 'LOYALTY_PROGRAM_HOW_TO_EARN_POINTS_MOBILE'
        },
        {
            key: 'loyaltyTiers',
            label: 'LOYALTY_PROGRAM_LOYALTY_TIERS_REWARDS',
            mobileLabel: 'LOYALTY_PROGRAM_LOYALTY_TIERS_REWARDS_MOBILE'
        },
        {
            key: 'longTerm',
            label: 'LOYALTY_PROGRAM_LONG_TERM_REWARDS',
            mobileLabel: 'LOYALTY_PROGRAM_LONG_TERM_REWARDS_MOBILE'
        },
        {
            key: 'rules',
            label: 'LOYALTY_PROGRAM_IMPORTANT_RULES',
            mobileLabel: 'LOYALTY_PROGRAM_IMPORTANT_RULES_MOBILE'
        }
    ];

    const earningActivities = [
        {
            activity: 'LOYALTY_PROGRAM_INVOICING_THROUGH_INVOZIO',
            points: 'LOYALTY_PROGRAM_POINTS_PER_SEK_INVOICED',
            notes: 'LOYALTY_PROGRAM_POINTS_AWARDED_AFTER_PAYMENT'
        },
        {
            activity: 'LOYALTY_PROGRAM_REFERRING_NEW_FREELANCER',
            points: 'LOYALTY_PROGRAM_POINTS_25000',
            notes: 'LOYALTY_PROGRAM_REFERRED_USER_MINIMUM_INVOICE'
        },
        {
            activity: 'LOYALTY_PROGRAM_POSTING_REVIEW_GOOGLE_TRUSTPILOT',
            points: 'LOYALTY_PROGRAM_POINTS_25000_PER_REVIEW',
            notes: 'LOYALTY_PROGRAM_MAX_2_REVIEWS_PER_USER'
        },
        {
            activity: 'LOYALTY_PROGRAM_CELEBRATING_BIRTHDAY',
            points: 'LOYALTY_PROGRAM_POINTS_25000_BIRTHDAY',
            notes: 'LOYALTY_PROGRAM_ONCE_PER_CALENDAR_YEAR'
        }
    ];

    const renderHowToEarnContent = () => (
        <div className="rules-panel">
            <div className="panel-head">
                <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_HOW_TO_EARN_POINTS')}</h4>
                <p className="panel-sub">{t('LOYALTY_PROGRAM_YOU_EARN_LOYALTY_POINTS_AUTOMATICALLY')}</p>
            </div>

            {/* Desktop Table View */}
            <div className="earning-table-wrapper desktop-only">
                <table className="earning-table" role="table">
                    <thead>
                        <tr>
                            <th scope="col">{t('LOYALTY_PROGRAM_ACTIVITY')}</th>
                            <th scope="col">{t('LOYALTY_PROGRAM_POINTS_EARNED')}</th>
                            <th scope="col">{t('LOYALTY_PROGRAM_NOTES')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {earningActivities.map((activity, index) => (
                            <tr key={index}>
                                <td>{t(activity.activity)}</td>
                                <td>{t(activity.points)}</td>
                                <td>{t(activity.notes)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-earning-cards mobile-only">
                {earningActivities.map((activity, index) => (
                    <div key={index} className="mobile-earning-card">
                        <div className="mobile-card-row">
                            <div className="mobile-card-label d-flex justify-content-start px-1">{t('LOYALTY_PROGRAM_ACTIVITY')}</div>
                            <div className="mobile-card-value">{t(activity.activity)}</div>
                        </div>
                        <div className="mobile-card-row">
                            <div className="mobile-card-label d-flex justify-content-start px-1">{t('LOYALTY_PROGRAM_POINTS_EARNED')}</div>
                            <div className="mobile-card-value">{t(activity.points)}</div>
                        </div>
                        <div className="mobile-card-row">
                            <div className="mobile-card-label d-flex justify-content-start px-1">{t('LOYALTY_PROGRAM_NOTES')}</div>
                            <div className="mobile-card-value">{t(activity.notes)}</div>
                        </div>
                    </div>
                ))}
            </div>

            <p className="panel-note">
                <span className="note-highlight">{t('LOYALTY_PROGRAM_NOTE')}:</span> {t('LOYALTY_PROGRAM_POINT_TOTALS_ROLLING_12_MONTHS')}
            </p>
        </div>
    );

    const medalLevels = [
        { tier: 'LOYALTY_PROGRAM_BRONZE_LEVEL', pointsNeeded: 250000, discount: '20%', extraBenefits: 'LOYALTY_PROGRAM_BRONZE_LEVEL_DESC' },
        { tier: 'LOYALTY_PROGRAM_SILVER_LEVEL', pointsNeeded: 500000, discount: '30%', extraBenefits: 'LOYALTY_PROGRAM_SILVER_LEVEL_DESC' },
        { tier: 'LOYALTY_PROGRAM_GOLD_LEVEL', pointsNeeded: 1000000, discount: '40%', extraBenefits: 'LOYALTY_PROGRAM_GOLD_LEVEL_DESC' },
        { tier: 'LOYALTY_PROGRAM_PLATINUM_LEVEL', pointsNeeded: 2000000, discount: '50%', extraBenefits: 'LOYALTY_PROGRAM_PLATINUM_LEVEL_DESC' }
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'howToEarn':
                return renderHowToEarnContent();
            case 'loyaltyTiers':
                return (
                    <div className="rules-panel">
                        <div className="panel-head">
                            <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_LOYALTY_TIERS_REWARDS')}</h4>
                            <p className="panel-sub">{t('LOYALTY_PROGRAM_LOYALTY_TIERS_DESCRIPTION')}</p>
                        </div>

                        {/* Desktop Table View */}
                        <div className="earning-table-wrapper desktop-only">
                            <table className="earning-table tiers-table" role="table">
                                <thead>
                                    <tr>
                                        <th scope="col">{t('LOYALTY_PROGRAM_TIER')}</th>
                                        <th scope="col">{t('LOYALTY_PROGRAM_POINTS_NEEDED')}</th>
                                        <th scope="col">{t('LOYALTY_PROGRAM_DISCOUNT')}</th>
                                        <th scope="col">{t('LOYALTY_PROGRAM_EXTRA_BENEFITS')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* <tr>
                                        <td>{t('LOYALTY_PROGRAM_IRON_LEVEL')}</td>
                                        <td>0</td>
                                        <td>0%</td>
                                        <td>{t('LOYALTY_PROGRAM_IRON_DESCRIPTION')}</td>
                                    </tr> */}
                                    <tr>
                                        <td>{t('LOYALTY_PROGRAM_BRONZE_LEVEL')}</td>
                                        <td>{formatAmount(250000)}</td>
                                        <td>20%</td>
                                        {/* <td>{t('LOYALTY_PROGRAM_BRONZE_LEVEL_DESC')}</td> */}
                                        <td>N/A</td>
                                    </tr>
                                    <tr>
                                        <td>{t('LOYALTY_PROGRAM_SILVER_LEVEL')}</td>
                                        <td>{formatAmount(500000)}</td>
                                        <td>30%</td>
                                        <td>N/A</td>
                                    </tr>
                                    <tr>
                                        <td>{t('LOYALTY_PROGRAM_GOLD_LEVEL')}</td>
                                        <td>{formatAmount(1000000)}</td>
                                        <td>40%</td>
                                        <td>{t('LOYALTY_PROGRAM_GOLD_LEVEL_DESCIRIPTION')}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('LOYALTY_PROGRAM_PLATINUM_LEVEL')}</td>
                                        <td>{formatAmount(2000000)}</td>
                                        <td>50%</td>
                                        <td>{t('LOYALTY_PROGRAM_PLATINUM_LEVEL_DESCRIPTION')}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards View */}
                        <div className="mobile-tiers-cards mobile-only">
                            {medalLevels.map((level, index) => (
                                <div key={index} className="mobile-tier-card">
                                    <div className="mobile-card-row">
                                        <div className="mobile-card-label">{t('LOYALTY_PROGRAM_TIER')}</div>
                                        <div className="mobile-card-value">{t(level.tier)}</div>
                                    </div>
                                    <div className="mobile-card-row">
                                        <div className="mobile-card-label">{t('LOYALTY_PROGRAM_POINTS_NEEDED')}</div>
                                        <div className="mobile-card-value">{formatAmount(level.pointsNeeded)}</div>
                                    </div>
                                    <div className="mobile-card-row">
                                        <div className="mobile-card-label">{t('LOYALTY_PROGRAM_DISCOUNT')}</div>
                                        <div className="mobile-card-value">{level.discount}</div>
                                    </div>
                                    <div className="mobile-card-row">
                                        <div className="mobile-card-label">{t('LOYALTY_PROGRAM_EXTRA_BENEFITS')}</div>
                                        <div className="mobile-card-value">{level.extraBenefits === 'LOYALTY_PROGRAM_GOLD_LEVEL_DESC' ? t('LOYALTY_PROGRAM_GOLD_LEVEL_DESCIRIPTION') : level.extraBenefits === 'LOYALTY_PROGRAM_PLATINUM_LEVEL_DESC' ? t('LOYALTY_PROGRAM_PLATINUM_LEVEL_DESCRIPTION') : 'N/A'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="panel-note">
                            <span className="note-highlight">{t('LOYALTY_PROGRAM_DISCOUNT_AUTO_APPLY')}</span> {t('LOYALTY_PROGRAM_DISCOUNT_AUTO_APPLY_TEXT')}
                        </p>
                        <p className="panel-note">
                            {t('LOYALTY_PROGRAM_DISCOUNT_LIMITATION')}.
                        </p>
                    </div>
                );
            case 'longTerm':
                return (
                    <div className="rules-panel">
                        <div className="panel-head">
                            <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_LONG_TERM_REWARDS_TITLE')}</h4>
                            <p className="panel-sub">{t('LOYALTY_PROGRAM_LONG_TERM_REWARDS_DESCRIPTION')}</p>
                        </div>
                        <div className="long-term-rewards-content">
                            <div className="reward-section">
                                <h5 className="reward-tier-title">{t('LOYALTY_PROGRAM_GOLD_REWARD_OPTIONS')}</h5>
                                <ul className="reward-list">
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_GOLD_GIFT_OPTION') }}></li>
                                    {/* <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_GOLD_ADVICE_OPTION') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_GOLD_BONUS_OPTION') }}></li> */}
                                </ul>
                            </div>
                            <div className="reward-section">
                                <h5 className="reward-tier-title">{t('LOYALTY_PROGRAM_PLATINUM_REWARD_OPTIONS')}</h5>
                                <ul className="reward-list">
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_PLATINUM_GIFT_OPTION') }}></li>
                                    {/* <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_PLATINUM_ADVICE_OPTION') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_PLATINUM_BONUS_OPTION') }}></li> */}
                                </ul>
                            </div>
                        </div>
                        {/* <p className="panel-note">{t('LOYALTY_PROGRAM_REWARD_NOTIFICATION')}</p> */}
                    </div>
                );
            case 'rules':
                return (
                    <div className="rules-panel">
                        <div className="panel-head">
                            <h4 className="panel-title earn-title">{t('LOYALTY_PROGRAM_IMPORTANT_RULES_TITLE')}</h4>
                        </div>

                        {/* Desktop View */}
                        <div className="important-rules-content desktop-only">
                            <div className="rules-section">
                                <ul className="rules-list">
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_POINTS_AFTER_PAYMENT') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_REFERRAL_THRESHOLD') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_UNIQUE_REVIEWS') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_INDIVIDUAL_ONLY') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_TIER_DOWNGRADE') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_EMAIL_WARNING') }}></li>
                                </ul>
                            </div>
                            <div className="mt-3">
                                <h5 className="Heading_h5">{t("INACTIVITY_AND_DEACTIVATION")}</h5>
                                <p className="text-black-500">{t('INACTIVITY_AND_DEACTIVATION_DESCRIPTION')}</p>
                            </div>
                        </div>

                        <div className="important-rules-content mobile-only">
                            <div className="rules-section">
                                <ul className="rules-list">
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_POINTS_AFTER_PAYMENT') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_REFERRAL_THRESHOLD') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_UNIQUE_REVIEWS') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_INDIVIDUAL_ONLY') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_TIER_DOWNGRADE') }}></li>
                                    <li dangerouslySetInnerHTML={{ __html: '✅ ' + t('LOYALTY_PROGRAM_RULE_EMAIL_WARNING') }}></li>
                                </ul>
                            </div>

                        </div>
                        {/* 
                        <div className="inactivity-section">
                            <h5 className="inactivity-title">{t('LOYALTY_PROGRAM_INACTIVITY_TITLE')}</h5>
                            <p className="inactivity-description" dangerouslySetInnerHTML={{ __html: t('LOYALTY_PROGRAM_INACTIVITY_DESCRIPTION') }}></p>
                        </div> */}
                    </div>
                );
            default:
                return renderHowToEarnContent();
        }
    };

    const handleTermsClick = () => {

        let pdfUrl = isEnglish ? 'https://invozioprod.blob.core.windows.net/website-assets/pdf/InvozioLoyaltyProgramEnglish.pdf' : 'https://invozioprod.blob.core.windows.net/website-assets/pdf/LojalitetsprogrammetSwedish.pdf';
        window.open(pdfUrl, '_blank');
    };

    return (
        <div className="loyalty-rules-root">
            <div className="rules-header">
                <h3 className="rules-heading">{t('LOYALTY_PROGRAM_RULES_TERMS')}</h3>
                <button className="terms-btn" type="button" onClick={handleTermsClick}>
                    <span className="label">{t('LOYALTY_PROGRAM_VIEW_TERMS_CONDITIONS')}</span>
                    <img src="/assets/svg/download-icon.svg" className="arrow-icon" alt="" aria-hidden="true" />
                </button>
            </div>
            <div className="rules-layout responsive-layout" role="tablist" aria-orientation="vertical">
                {/* Mobile Horizontal Tabs */}
                <div className="mobile-tabs-nav mobile-only">
                    {tabs.map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={active}
                                className={`mobile-tab-btn${active ? ' active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <span
                                    className="mobile-tab-label"
                                    dangerouslySetInnerHTML={{ __html: t(tab.mobileLabel) }}
                                />
                            </button>
                        );
                    })}
                </div>

                {/* Desktop Sidebar Tabs - becomes horizontal on zoom/smaller screens */}
                <div className="rules-sidebar desktop-only responsive-tabs">
                    {tabs.map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={active}
                                className={`rules-tab-btn${active ? ' active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <span className="label">{t(tab.label)}</span>
                                {active && <img src="/assets/svg/right-minimal.svg" className="arrow" alt="" aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
                <div className="rules-panel-wrapper" role="tabpanel">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default LoyaltyRulesSection;
